import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, XCircle, Clock, Loader2 } from "lucide-react";
import { confirmDeletion } from "@/services/accountDeletionService";
import { Button } from "@/components/ui/button";
import { usePageTitle } from "@/hooks/usePageTitle";

type Status = "pending" | "success" | "expired" | "invalid" | "error";

export default function DeletionConfirmationPage() {
  usePageTitle('Account Deleted | BookMyCoaching');
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const [status, setStatus] = useState<Status>("pending");
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }
    confirmDeletion(token)
      .then(() => setStatus("success"))
      .catch((err: Error) => {
        const msg = err.message ?? "";
        if (msg.includes("410") || msg.toLowerCase().includes("expired")) {
          setStatus("expired");
        } else if (msg.includes("404") || msg.toLowerCase().includes("not found")) {
          setStatus("invalid");
        } else {
          setStatus("error");
          setErrorMessage(msg);
        }
      });
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-md w-full text-center space-y-6">
        {status === "pending" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-primary animate-spin" />
            <h1 className="text-xl font-semibold text-foreground">Processing…</h1>
            <p className="text-sm text-muted-foreground">
              Please wait while we process your deletion request.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle className="w-12 h-12 mx-auto text-green-500" />
            <h1 className="text-xl font-semibold text-foreground">Data deleted</h1>
            <p className="text-sm text-muted-foreground">
              Your data has been anonymised and removed from our systems. If you still have
              another role active, you can continue using BookMyCoaching with that role.
            </p>
            <Button asChild variant="default">
              <Link to="/">Go to home</Link>
            </Button>
          </>
        )}

        {status === "expired" && (
          <>
            <Clock className="w-12 h-12 mx-auto text-yellow-500" />
            <h1 className="text-xl font-semibold text-foreground">Link expired</h1>
            <p className="text-sm text-muted-foreground">
              This deletion link has expired. Links are valid for 24 hours.
              Please go to your settings and request a new deletion link.
            </p>
            <Button asChild variant="outline">
              <Link to="/settings">Back to settings</Link>
            </Button>
          </>
        )}

        {status === "invalid" && (
          <>
            <XCircle className="w-12 h-12 mx-auto text-destructive" />
            <h1 className="text-xl font-semibold text-foreground">Invalid link</h1>
            <p className="text-sm text-muted-foreground">
              This deletion link is invalid or has already been used.
              If you need to delete your data, please submit a new request from settings.
            </p>
            <Button asChild variant="outline">
              <Link to="/settings">Back to settings</Link>
            </Button>
          </>
        )}

        {status === "error" && (
          <>
            <XCircle className="w-12 h-12 mx-auto text-destructive" />
            <h1 className="text-xl font-semibold text-foreground">Something went wrong</h1>
            <p className="text-sm text-muted-foreground">
              {errorMessage || "An unexpected error occurred. Please try again later."}
            </p>
            <Button asChild variant="outline">
              <Link to="/settings">Back to settings</Link>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
